import { Box, Section, Spinner, Text } from '@factorialco/deprecated-design-system'
import { DocumentFragment, PayrollDocumentFragment } from 'generated/resources/graphql'
import { PayrollBundleA3innuvaButtonDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import getTitle from 'modules/documents/components/Documents/getTitle'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

import FileBox from './FileBox'

interface Props {
  policyId: string
  loading: boolean
  documents: DocumentFragment[]
  payrollDocuments: PayrollDocumentFragment[]
  reloadFiles: () => void
  periodId: string
  downloadedDocuments: DocumentFragment[] | null
  companyId: string
}

const DOCUMENT_OPTION = 'uploaded'

const PayrollCalculationStep = ({
  policyId,
  loading,
  documents,
  payrollDocuments,
  reloadFiles,
  periodId,
  downloadedDocuments,
  companyId,
}: Props) => {
  const { navigation } = useNavigation()
  const bookkeeperContext = {
    isBookkeeperView: true,
  }
  const documentsGroupByFolder = documents.reduce(
    (group: Record<string, DocumentFragment[]>, document) => {
      if (document.folder?.id) {
        const folderId = document.folder.id.toString()
        const groupArray = group[folderId] ?? []
        groupArray.push(document)
        group[folderId] = groupArray
      }
      return group
    },
    {}
  )

  const folderTitle = (folderName: string | null | undefined) => {
    return getTitle(folderName || '')
  }

  const buildExportLink = () => {
    return navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.export.fullPath(
      {
        periodId,
        policyId,
      }
    )
  }

  const buildSyncLink = () => {
    return navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.sync.fullPath(
      {
        periodId,
        policyId,
      }
    )
  }

  const buildUploadLink = () => {
    return navigation.bookkeepers.clients.payrollResults.payrollPolicy.payrollPolicyPeriodId.upload.fullPath(
      {
        periodId,
        policyId,
      }
    )
  }

  const { data } = useQuery(PayrollBundleA3innuvaButtonDocument, {
    context: bookkeeperContext,
    variables: {
      companyId: companyId,
      datoIds: [integrationCodes.a3nom, integrationCodes.a3innuva],
    },
  })

  const integrationsData = data?.marketplace?.integrationsConnection?.nodes || []
  const companyInstallationsData = data?.marketplace?.companyInstallationsConnection?.nodes || []

  interface Integration {
    id: string
    integrationId: string
    formData?: string | null
  }
  interface Integrations {
    [key: string]: Integration
  }

  const installedIntegrations = integrationsData.reduce((acc: Integrations, current) => {
    const integration = companyInstallationsData.find((i) => i.integrationId === current.id)
    if (!integration) return acc

    acc[current.name.toLocaleLowerCase()] = integration
    return acc
  }, {})

  const tooltip_visible = installedIntegrations.hasOwnProperty('a3nom')
    ? undefined
    : i18n.t('bookkeepers.clients.payroll_periods.calculation_step.zero.tooltip')

  return (
    <>
      {installedIntegrations.hasOwnProperty('a3innuva nómina') && (
        <Box
          border={{
            bottom: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          <Section
            illustration="URL"
            title={i18n.t('bookkeepers.clients.payroll_periods.calculation_step.zero.title')}
            description={i18n.t(
              'bookkeepers.clients.payroll_periods.calculation_step.zero.description'
            )}
            action={{
              label: i18n.t('bookkeepers.clients.payroll_periods.calculation_step.zero.action'),
              to: buildSyncLink(),
              tooltip: tooltip_visible,
              disabled: !installedIntegrations.hasOwnProperty('a3nom'),
            }}
          >
            {loading ? (
              <Spinner />
            ) : (
              <>
                {downloadedDocuments && (
                  <Box gap="s24">
                    <Box gap="s12">
                      <Text.Normal size="200" weight="medium">
                        {i18n.t('bookkeepers.clients.payroll_periods.downloaded_files')}
                      </Text.Normal>
                      {downloadedDocuments.reverse().map((document) => (
                        <FileBox
                          document={document}
                          isDeleteAble={false}
                          key={document.id}
                          documentOption={DOCUMENT_OPTION}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Section>
        </Box>
      )}
      <Box
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Section
          illustration="download"
          title={i18n.t('bookkeepers.clients.payroll_periods.calculation_step.first.title')}
          description={i18n.t(
            'bookkeepers.clients.payroll_periods.calculation_step.first.description'
          )}
          action={{
            label: i18n.t('bookkeepers.clients.payroll_periods.calculation_step.first.action'),
            to: buildExportLink(),
          }}
        >
          {loading ? (
            <Spinner />
          ) : (
            <>
              {downloadedDocuments && (
                <Box gap="s24">
                  <Box gap="s12">
                    <Text.Normal size="200" weight="medium">
                      {i18n.t('bookkeepers.clients.payroll_periods.downloaded_files')}
                    </Text.Normal>
                    {downloadedDocuments.reverse().map((document) => (
                      <FileBox
                        document={document}
                        isDeleteAble={false}
                        key={document.id}
                        documentOption={DOCUMENT_OPTION}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Section>
      </Box>
      <Section
        illustration="file-upload"
        title={i18n.t('bookkeepers.clients.payroll_periods.calculation_step.second.title')}
        description={i18n.t(
          'bookkeepers.clients.payroll_periods.calculation_step.second.description'
        )}
        action={{
          label: i18n.t('bookkeepers.clients.payroll_periods.calculation_step.second.action'),
          to: buildUploadLink(),
        }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box gap="s24">
            {Object.keys(documentsGroupByFolder).map(
              (folderId: keyof typeof documentsGroupByFolder) => {
                const groupedDocuments = documentsGroupByFolder[folderId] || []
                const firstDocument = groupedDocuments[0]

                return (
                  <Box key={folderId} gap="s12">
                    <Text.Normal size="200" weight="medium">
                      {folderTitle(firstDocument?.folder?.name)}
                    </Text.Normal>
                    {groupedDocuments?.reverse().map((document) => (
                      <FileBox
                        document={document}
                        key={document.id}
                        payrollDocuments={payrollDocuments}
                        reloadFiles={reloadFiles}
                      />
                    ))}
                  </Box>
                )
              }
            )}
          </Box>
        )}
      </Section>
    </>
  )
}

export default PayrollCalculationStep
